import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { CheckCircle, XCircle, Loader, Plus, X } from "react-feather";
import { Navbar } from "./Navbar";

const api = process.env.REACT_APP_API_URL;

const KeywordReview = () => {
  const navigate = useNavigate();

  const [keywords,     setKeywords]     = useState([]);
  const [decisions,    setDecisions]    = useState({});
  const [customInput,  setCustomInput]  = useState("");
  const [customKws,    setCustomKws]    = useState([]); // user-added keywords
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);

  useEffect(() => {
    axios
      .get(`${api}missing-keywords/`, { withCredentials: true })
      .then((res) => { setKeywords(res.data.missing_keywords || []); setLoading(false); })
      .catch(() => { toast.error("Could not load keywords. Please try again."); setLoading(false); });
  }, []);

  const decide = (keyword, decision) =>
    setDecisions((prev) => ({ ...prev, [keyword]: decision }));

  const allKeywords = [...keywords, ...customKws];
  const reviewed    = Object.keys(decisions).length;
  const approved    = Object.values(decisions).filter((d) => d === "approved").length;

  const approveAll = () => {
    const all = {};
    allKeywords.forEach((k) => { all[k] = "approved"; });
    setDecisions(all);
  };
  const clearAll = () => setDecisions({});

  // ── Add custom keyword ────────────────────────────────────────────────────
  const addCustom = () => {
    const kw = customInput.trim();
    if (!kw) return;
    if (allKeywords.includes(kw)) {
      toast.error("That keyword already exists.");
      return;
    }
    setCustomKws((prev) => [...prev, kw]);
    setDecisions((prev) => ({ ...prev, [kw]: "approved" })); // auto-approve custom keywords
    setCustomInput("");
  };

  const removeCustom = (kw) => {
    setCustomKws((prev) => prev.filter((k) => k !== kw));
    setDecisions((prev) => { const next = { ...prev }; delete next[kw]; return next; });
  };

  const handleBuildResume = async () => {
    const approvedKeywords = allKeywords.filter((k) => decisions[k] === "approved");
    if (approvedKeywords.length === 0) {
      toast.error("Please approve at least one keyword.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${api}save-keyword-selection/`,
        { approved_keywords: approvedKeywords },
        { withCredentials: true }
      );
      const steps = JSON.parse(localStorage.getItem("completedSteps") || "[]");
      if (!steps.includes("keywordReview")) { steps.push("keywordReview"); localStorage.setItem("completedSteps", JSON.stringify(steps)); }
      navigate("/ats-resume");
    } catch {
      toast.error("Failed to save selections. Please try again.");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="animate-spin text-blue-600 w-10 h-10" />
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white px-4 py-10">
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Review Your Keywords</h1>
            <p className="text-gray-500">
              Approve keywords to add to your resume. Skip any that don't apply — or add your own below.
            </p>
          </div>

          {/* Progress bar */}
          <div className="bg-white rounded-2xl shadow p-4 mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>{reviewed} of {allKeywords.length} reviewed</span>
              <span className="text-green-600 font-semibold">{approved} approved</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${allKeywords.length ? (reviewed / allKeywords.length) * 100 : 0}%` }}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={approveAll} className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 font-medium transition">
                Approve All
              </button>
              <button onClick={clearAll} className="text-xs px-3 py-1 bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200 font-medium transition">
                Clear All
              </button>
            </div>
          </div>

          {/* AI keyword cards */}
          <div className="space-y-3 mb-6">
            {keywords.map((kw) => (
              <KeywordCard key={kw} keyword={kw} decision={decisions[kw]} onDecide={decide} />
            ))}
          </div>

          {/* Custom keywords section */}
          <div className="bg-white rounded-2xl shadow p-5 mb-8">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4 text-violet-500" />
              Add Your Own Keywords
              <span className="text-xs text-gray-400 font-normal ml-1">— auto-approved</span>
            </h3>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCustom()}
                placeholder="e.g. Agile, Tableau, stakeholder management…"
                className="flex-1 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-gray-50"
              />
              <button
                onClick={addCustom}
                disabled={!customInput.trim()}
                className="px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-500 disabled:opacity-40 transition"
              >
                Add
              </button>
            </div>
            {customKws.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {customKws.map((kw) => (
                  <span key={kw} className="flex items-center gap-1.5 bg-violet-100 text-violet-700 text-xs font-medium px-3 py-1.5 rounded-full border border-violet-200">
                    {kw}
                    <button onClick={() => removeCustom(kw)} className="hover:text-red-500 transition">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Build button */}
          <div className="text-center">
            <button
              onClick={handleBuildResume}
              disabled={approved === 0 || saving}
              className="px-8 py-3 bg-blue-600 text-white text-lg font-semibold rounded-xl hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {saving
                ? <span className="flex items-center gap-2 justify-center"><Loader className="animate-spin w-5 h-5" /> Saving…</span>
                : `Build My ATS Resume (${approved} keyword${approved !== 1 ? "s" : ""})`}
            </button>
            <p className="mt-3 text-sm text-gray-400">You can build with approved keywords without reviewing all of them.</p>
          </div>

        </div>
      </div>
    </>
  );
};

function KeywordCard({ keyword, decision, onDecide }) {
  return (
    <div className={`flex items-center justify-between bg-white rounded-xl shadow px-5 py-4 border-2 transition-all ${
      decision === "approved" ? "border-green-400 bg-green-50" : decision === "skipped" ? "border-gray-200 opacity-60" : "border-transparent"
    }`}>
      <span className="text-gray-800 font-medium">{keyword}</span>
      <div className="flex space-x-2">
        <button
          onClick={() => onDecide(keyword, "approved")}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
            decision === "approved" ? "bg-green-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-700"
          }`}
        >
          <CheckCircle className="w-4 h-4" /> Approve
        </button>
        <button
          onClick={() => onDecide(keyword, "skipped")}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
            decision === "skipped" ? "bg-gray-400 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          <XCircle className="w-4 h-4" /> Skip
        </button>
      </div>
    </div>
  );
}

export default KeywordReview;
